import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import {
  Email as EmailIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { selectMfaConfigs, selectSelectedMfaType } from 'base/mfa/selectors';
import { mfaSliceActions } from 'base/mfa/slice';
import { PrimaryLiteButton, PrimaryLoadingButton } from 'components';

interface MfaConfig {
  userId: string;
  mfaType: 'EMAIL' | 'TOTP';
  createdAt: string;
  backupCount: number;
  isEnabled: boolean;
  verifiedAt: string;
}

interface MfaSelectionViewProps {
  onMfaTypeSelected: (mfaType: 'EMAIL' | 'TOTP') => void;
  onBackToSignIn: () => void;
}

const MfaSelectionView: React.FC<MfaSelectionViewProps> = (
  { onMfaTypeSelected, onBackToSignIn },
) => {
  const dispatch = useDispatch();
  const mfaConfigs = useSelector(selectMfaConfigs);
  const selectedMfaType = useSelector(selectSelectedMfaType);
  const hasAutoSelected = useRef(false);

  useEffect(() => {
    const hasOneConfig = mfaConfigs && mfaConfigs.length === 1;
    const noSelection = !selectedMfaType;
    const notAutoSelected = !hasAutoSelected.current;

    if (hasOneConfig && noSelection && notAutoSelected) {
      const singleMfaType = mfaConfigs[0].mfaType;
      hasAutoSelected.current = true;
      dispatch(mfaSliceActions.selectMfaType({ mfaType: singleMfaType }));
      onMfaTypeSelected(singleMfaType);
    }
  }, [mfaConfigs, selectedMfaType, dispatch, onMfaTypeSelected]);

  const handleMfaTypeSelect = (mfaType: 'EMAIL' | 'TOTP') => {
    dispatch(mfaSliceActions.selectMfaType({ mfaType }));
  };

  const handleContinue = () => {
    if (selectedMfaType) {
      onMfaTypeSelected(selectedMfaType);
    }
  };

  const getMfaTypeLabel = (mfaType: 'EMAIL' | 'TOTP') => (
    mfaType === 'EMAIL' ? 'Email Verification' : 'Authenticator App'
  );

  const getMfaTypeDescription = (mfaType: 'EMAIL' | 'TOTP') => (
    mfaType === 'EMAIL'
      ? 'Receive a verification code via email'
      : 'Use your authenticator app to generate a code'
  );

  const getMfaTypeIcon = (mfaType: 'EMAIL' | 'TOTP') => (
    mfaType === 'EMAIL' ? <EmailIcon /> : <SecurityIcon />
  );

  const getBackupCodeInfo = (config: MfaConfig) => {
    if (config.backupCount > 0) {
      return `${config.backupCount} backup codes available`;
    }
    return 'No backup codes available';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          {mfaConfigs.map((config) => (
            <Grid item xs={12} key={config.mfaType}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                  ...(selectedMfaType === config.mfaType && {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  }),
                }}
              >
                <CardActionArea onClick={() => handleMfaTypeSelect(config.mfaType)} sx={{ p: 0 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          color: 'primary.main',
                          mt: 0.25,
                          '& svg': { fontSize: 20 },
                        }}
                      >
                        {getMfaTypeIcon(config.mfaType)}
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" component="h3" sx={{ mb: 0.5 }}>
                          {getMfaTypeLabel(config.mfaType)}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {getMfaTypeDescription(config.mfaType)}
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            label={config.isEnabled ? 'Enabled' : 'Disabled'}
                            color={config.isEnabled ? 'success' : 'default'}
                            size="small"
                          />

                          {config.backupCount > 0 && config.mfaType === 'TOTP' && (
                            <Chip
                              label={getBackupCodeInfo(config)}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <PrimaryLiteButton
            startIcon={<ArrowBackIcon />}
            onClick={onBackToSignIn}
            sx={{ minWidth: 140 }}
          >
            Back to Sign In
          </PrimaryLiteButton>

          <PrimaryLoadingButton
            onClick={handleContinue}
            sx={{ minWidth: 120 }}
            disabled={!selectedMfaType}
          >
            Continue
          </PrimaryLoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MfaSelectionView;
